/* eslint-disable react/prop-types */
import React, { useRef } from 'react';
import { Section, F } from '../CommonUI';
import { WEEKDAYS } from '../../constants';
import { resolveEmployeeImageSrc } from '../../../../utils/imageFile.util';

/**
 * Tab 3: Contact & Other Details component
 * Extracted from EmployeeMaster main file to improve maintainability.
 */
function ContactOtherTab({ d, ch, off, opts }) {
  const photoRef = useRef();
  const sigRef = useRef();
  const isCheckedValue = v =>
    v === true || v === 1 || v === '1' || v === 'Y' || v === 'y';
  const weeklyHolidayView = d.weeklyHoliday || d.weeklyHolidayApi;

  const handleFile = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      const r = new FileReader();
      r.onload = ev => ch(field, ev.target.result);
      r.readAsDataURL(file);
    }
  };

  const handleWeekday = (day, type) => {
    const wh = { ...d.weeklyHoliday };
    wh[day] = { ...wh[day], [type]: !isCheckedValue(wh[day]?.[type]) };
    ch('weeklyHoliday', wh);
    ch('weeklyHolidayApi', wh);
  };

  return (
    <div className="tb anim-fade">
      <div className="c-top">
        {/* Passport Column */}
        <div className="o-stack">
          <Section
            title="Passport Details"
            cols={1}
            icon={
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <rect
                  x="2"
                  y="3"
                  width="20"
                  height="18"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <circle
                  cx="12"
                  cy="11"
                  r="3"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M7 17.5c0-2 2.24-3.5 5-3.5s5 1.5 5 3.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            }
          >
            <F
              label="Passport No."
              name="passportNo"
              value={d.passportNo}
              onChange={ch}
              disabled={off}
              maxLength={20}
            />
            <F
              label="Issue Place"
              name="issuePlace"
              value={d.issuePlace}
              onChange={ch}
              options={opts.issuePlace}
              disabled={off}
            />
            <F
              label="Issue Date"
              name="issueDate"
              value={d.issueDate}
              onChange={ch}
              type="date"
              disabled={off}
            />
            <F
              label="Expiry Date"
              name="expiryDate"
              value={d.expiryDate}
              onChange={ch}
              type="date"
              disabled={off}
            />
          </Section>
        </div>

        {/* Contact & Other Column */}
        <div className="o-stack">
          <Section
            title="Contact Information"
            cols={2}
            icon={
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path
                  d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.13.81.37 1.6.7 2.35a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.75.33 1.54.57 2.35.7A2 2 0 0122 16.92z"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            }
          >
            <F
              label="Local (Mobile)"
              name="localMobile"
              value={d.localMobile}
              onChange={ch}
              disabled={off}
              maxLength={50}
              numericOnly
              hideInvalidFormatMessage
            />
            <F
              label="Outstation (Mobile)"
              name="outstationMobile"
              value={d.outstationMobile}
              onChange={ch}
              disabled={off}
              maxLength={50}
              numericOnly
              hideInvalidFormatMessage
            />
            <F
              label="Email"
              name="email"
              value={d.email}
              onChange={ch}
              type="email"
              disabled={off}
              maxLength={100}
              style={{ gridColumn: 'span 2' }}
              pattern="^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
              hideInvalidFormatMessage
            />
          </Section>

          <Section
            title="Other Details"
            cols={2}
            icon={
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 7V4h16v3M9 20h6M12 4v16"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
          >
            <F
              label="Arabic Name"
              name="arabicName"
              value={d.arabicName}
              onChange={ch}
              disabled={off}
              maxLength={250}
            />
            <F
              label="Sponsor"
              name="sponsor"
              value={d.sponsor}
              onChange={ch}
              options={opts.sponsor}
              disabled={off}
            />
          </Section>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '16px',
        }}
      >
        <Section
          title="Employee Photo"
          cols={1}
          icon={
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <rect
                x="3"
                y="3"
                width="18"
                height="18"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
              />
              <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
              <path
                d="M21 15l-5-5L5 21"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        >
          <div
            className="photo-upload"
            onClick={() => !off && photoRef.current?.click()}
          >
            {d.photo ? (
              <img
                src={resolveEmployeeImageSrc(d.photo)}
                alt="Employee"
                className="photo-img"
              />
            ) : (
              <div className="photo-ph">
                <svg width="40" height="40" fill="none" viewBox="0 0 24 24">
                  <path
                    d="M12 12c2.7 0 5-2.3 5-5s-2.3-5-5-5-5 2.3-5 5 2.3 5 5 5zm0 2c-3.3 0-10 1.7-10 5v2h20v-2c0-3.3-6.7-5-10-5z"
                    fill="#cbd5e1"
                  />
                </svg>
                <span className="photo-ph-t">Click to upload photo</span>
                <span className="photo-ph-s">JPG, PNG up to 5MB</span>
              </div>
            )}
            <input
              ref={photoRef}
              type="file"
              accept="image/*"
              onChange={e => handleFile(e, 'photo')}
              hidden
            />
          </div>
        </Section>

        <Section
          title="Signature"
          cols={1}
          icon={
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path
                d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          }
        >
          <div
            className="sig-box"
            onClick={() => !off && sigRef.current?.click()}
          >
            {d.signature ? (
              <img
                src={resolveEmployeeImageSrc(d.signature)}
                alt="Signature"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <span>Click to upload signature</span>
            )}
            <input
              ref={sigRef}
              type="file"
              accept="image/*"
              onChange={e => handleFile(e, 'signature')}
              hidden
            />
          </div>
        </Section>

        <Section
          title="Weekly Holiday"
          cols={1}
          icon={
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <rect
                x="3"
                y="4"
                width="18"
                height="18"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M16 2v4M8 2v4M3 10h18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          }
        >
          <div className="wh">
            <div className="wh-h">
              <span />
              <span>F</span>
              <span>H</span>
            </div>
            {WEEKDAYS.map(dy => (
              <div key={dy} className="wh-r">
                <span>{dy.charAt(0).toUpperCase() + dy.slice(1)}</span>
                <input
                  type="checkbox"
                  checked={isCheckedValue(weeklyHolidayView?.[dy]?.f)}
                  onChange={() => {
                    if (off) return;
                    handleWeekday(dy, 'f');
                  }}
                  aria-disabled={off}
                />
                <input
                  type="checkbox"
                  checked={isCheckedValue(weeklyHolidayView?.[dy]?.h)}
                  onChange={() => {
                    if (off) return;
                    handleWeekday(dy, 'h');
                  }}
                  aria-disabled={off}
                />
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}

export default ContactOtherTab;
